<template>
  <div>
    <BaseSelect
      :options="formattedOptions"
      :model-value="modelValue"
      :placeholder="placeholder"
      :searchable="true"
      :clearable="clearable"
      :with-button="withButton"
      :button-label="buttonLabel"
      :no-results-text="noResultsText"
      :external-search="true"
      @update:model-value="handleUpdate"
      @change="handleChange"
      @button-click="openModal"
      @search="handleSearch"
      @clear-search="handleClearSearch"
      @dropdown-scroll-end="handleScrollEnd"
    />

    <add-reference-item-modal
      v-if="isModalVisible"
      :target-model="targetModel"
      :target-field="targetField"
      @item-created="handleItemCreated"
      @close="isModalVisible = false"
    />
  </div>
</template>

<script>
import { mapState } from "pinia";

import { useReferenceItemsStore } from "@/stores/referenceItemsStore";
import { getCompanyId } from "@/utils/userUtils";
import referenceItemsApi from "@/services/api/referenceItemsApi";

import AddReferenceItemModal from "@/components/ReferenceItem/AddReferenceItemModal.vue";

export default {
  name: "ReferenceItemSelect",
  components: {
    AddReferenceItemModal,
  },
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    targetModel: {
      type: String,
      required: true,
    },
    targetField: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: "Выберите значение",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Создать новый тип",
    },
    noResultsText: {
      type: String,
      default: "Ничего не найдено",
    },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      companyId: getCompanyId(),
      preselectedOption: null,
      isSearchActive: false,
      isModalVisible: false,
    };
  },
  computed: {
    ...mapState(useReferenceItemsStore, [
      "selectItems",
      "selectPagination",
      "selectIsLoading",
    ]),

    formattedOptions() {
      const options = this.selectItems.map((item) => ({
        value: item.id,
        label: item.name || `#${item.id}`,
        color: item.color,
      }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },
  watch: {
    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }
      if (newVal === oldVal) return;

      await this.fetchPreselectedOption();
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }
      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
          color: option.color,
        };
      } else {
        this.preselectedOption = null;
      }
      this.$emit("change", option ? option.value : null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      const store = useReferenceItemsStore();
      await store.fetchSelectItems({
        search: searchData.query,
        targetModel: this.targetModel,
        targetField: this.targetField,
        enabled: true,
      });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      const store = useReferenceItemsStore();
      await store.fetchSelectItems({
        targetModel: this.targetModel,
        targetField: this.targetField,
        enabled: true,
      });
    },

    async handleScrollEnd() {
      const store = useReferenceItemsStore();
      if (
        store.selectPagination.page < store.selectPagination.pages &&
        !store.selectIsLoading
      ) {
        await store.loadMoreSelectItems(
          this.targetModel,
          this.targetField,
          { enabled: true }
        );
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const store = useReferenceItemsStore();
      const exists = store.selectItems.find(
        (i) => String(i.id) === String(this.modelValue)
      );
      if (exists) {
        this.preselectedOption = {
          value: exists.id,
          label: exists.name || `#${exists.id}`,
          color: exists.color,
        };
        return;
      }

      try {
        const response = await referenceItemsApi.show(
          this.companyId,
          this.modelValue
        );
        const item = response.data.result || response.data;
        this.preselectedOption = {
          value: item.id,
          label: item.name || `#${item.id}`,
          color: item.color,
        };
      } catch (error) {
        console.error(
          "Ошибка при загрузке выбранного элемента:",
          error
        );
      }
    },

    openModal() {
      this.isModalVisible = true;
    },

    async handleItemCreated(newItem) {
      const store = useReferenceItemsStore();
      await store.fetchSelectItems({
        targetModel: this.targetModel,
        targetField: this.targetField,
        enabled: true,
      });

      this.preselectedOption = {
        value: newItem.id,
        label: newItem.name || `#${newItem.id}`,
        color: newItem.color,
      };
      this.$emit("update:modelValue", newItem.id);
      this.$emit("change", newItem.id);
    },
  },
  async mounted() {
    const store = useReferenceItemsStore();
    await store.fetchSelectItems({
      targetModel: this.targetModel,
      targetField: this.targetField,
      enabled: true,
    });
    if (this.modelValue) {
      await this.fetchPreselectedOption();
    }
  },
};
</script>
